import { Card, Empty, Skeleton, Typography } from "@douyinfe/semi-ui";
import { VChart } from "@visactor/react-vchart";
import { DashboardFilters, HomeDashboardApi, SliceItem } from "../api/homeDashboard";
import { useDashboardModule } from "../hooks/useDashboardModule";
import { chartBoxStyle, chartTitleStyle, PALETTE_QUALITY, PALETTE_INTENTION } from "../styles";
import { DrawerMode } from "./DetailDrawer";

interface Props {
  filters: DashboardFilters;
  isAdmin: boolean;
  currentTeamId?: number;
  openDrawer: (title: string, params: Record<string, any>, mode?: DrawerMode) => void;
}

const QUALITY_KEYS: { key: keyof QualityDaily; label: string }[] = [
  { key: "excellent", label: "优质" },
  { key: "good", label: "良好" },
  { key: "normal", label: "一般" },
  { key: "poor", label: "较差" },
  { key: "noScore", label: "无评分" },
];

const INTENTION_KEYS: { key: keyof IntentionDaily; label: string }[] = [
  { key: "a", label: "高意向" },
  { key: "b", label: "中高" },
  { key: "c", label: "中" },
  { key: "d", label: "低" },
  { key: "e", label: "低意向" },
  { key: "pending", label: "待定" },
];

interface QualityDaily { date: string; excellent: number; good: number; normal: number; poor: number; noScore: number; }
interface IntentionDaily { date: string; a: number; b: number; c: number; d: number; e: number; pending: number; }

const QUALITY_SCORE_RANGE: Record<string, { minScore?: number; maxScore?: number }> = {
  excellent: { minScore: 80 },
  good: { minScore: 60, maxScore: 79 },
  normal: { minScore: 40, maxScore: 59 },
  poor: { maxScore: 39 },
  noScore: {},
};

export default function AiAnalysisCard({ filters, openDrawer }: Props) {
  const q = useDashboardModule(
    () => HomeDashboardApi.getAiQualityDist(filters),
    [filters.startDate, filters.endDate, filters.teamId, JSON.stringify(filters.saleIds)],
    !!filters.startDate
  );
  const i = useDashboardModule(
    () => HomeDashboardApi.getAiIntentionDist(filters),
    [filters.startDate, filters.endDate, filters.teamId, JSON.stringify(filters.saleIds)],
    !!filters.startDate
  );

  return (
    <Card>
      <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
        <div style={chartBoxStyle}>
          <Typography.Text style={chartTitleStyle}>客户质量占比</Typography.Text>
          {q.loading && <Skeleton.Title />}
          {q.data && q.data.dist.length > 0 ? (
            <PieWithTrend
              dist={q.data.dist}
              daily={reshapeDaily(q.data.daily as QualityDaily[], QUALITY_KEYS)}
              palette={PALETTE_QUALITY}
              onClick={(s: SliceItem) => openDrawer(`AI - 客户质量 ${s.label}`, { ...filters, ...(QUALITY_SCORE_RANGE[s.key] || {}) })}
            />
          ) : !q.loading ? <Empty title="暂无质量分布数据" /> : null}
        </div>
        <div style={chartBoxStyle}>
          <Typography.Text style={chartTitleStyle}>客户意向占比</Typography.Text>
          {i.loading && <Skeleton.Title />}
          {i.data && i.data.dist.length > 0 ? (
            <PieWithTrend
              dist={i.data.dist}
              daily={reshapeDaily(i.data.daily as IntentionDaily[], INTENTION_KEYS)}
              palette={PALETTE_INTENTION}
              onClick={(s: SliceItem) => openDrawer(`AI - 客户意向 ${s.label}`, { ...filters, intentionLevel: (s.key || "").toUpperCase() })}
            />
          ) : !i.loading ? <Empty title="暂无意向分布数据" /> : null}
        </div>
      </div>
    </Card>
  );
}

function reshapeDaily<T extends { date: string }>(
  daily: T[],
  seriesKeys: { key: keyof T; label: string }[]
): { date: string; series: string; value: number }[] {
  const out: { date: string; series: string; value: number }[] = [];
  daily.forEach(d => {
    seriesKeys.forEach(({ key, label }) => {
      out.push({ date: d.date, series: label, value: (d[key] as number) ?? 0 });
    });
  });
  return out;
}

function PieWithTrend({
  dist, daily, palette, onClick,
}: {
  dist: SliceItem[];
  daily: { date: string; series: string; value: number }[];
  palette: string[];
  onClick: (s: SliceItem) => void;
}) {
  return (
    <>
      <VChart spec={{
        type: "pie", data: [{ values: dist.map(d => ({ name: d.label, value: d.count })) }],
        valueField: "value", categoryField: "name",
        color: { range: palette },
        label: { visible: true, position: "outside", text: "{name}: {value}" },
        legends: [{ visible: true, position: "right" }],
      } as any} style={{ height: 200 }}
        onClick={(e: any) => {
          const name = e?.datum?.["name"];
          const r = dist.find(x => x.label === name);
          if (r) onClick(r);
        }} />
      {daily.length > 0 && (
        <VChart spec={{
          type: "bar", data: [{ values: daily }],
          xField: "date", yField: "value", seriesField: "series",
          stack: true,
          color: { range: palette },
          label: { visible: true, position: "inside", text: "{value}" },
          legends: [{ visible: true, position: "top" }],
        } as any} style={{ height: 160, marginTop: 6 }} />
      )}
    </>
  );
}
